import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Briefcase, ExternalLink, FileText, Loader2 } from 'lucide-react';
import {
  ApiError,
  Badge,
  DataTable,
  Drawer,
  EmptyState,
  Pagination,
  useToast,
  APPLICATION_STATUS_LABELS,
  CANDIDATE_APPLICATION_STATUS_LABELS,
  CANDIDATE_APPLICATION_STATUS_TONE,
  type ApplicationStatus,
  type CandidateApplicationStatus,
  type Column,
} from '@qh/shared';
import {
  candidateApi,
  type ApplicationDetail,
  type ApplicationListItem,
  type ApplicationsListParams,
} from '../../lib/apiClient';
import { formatRelativeTime } from '../../lib/relativeTime';

/**
 * My Job Applications (analysis §5.5) — paginated list with optional status / search
 * filters and a detail drawer that surfaces the status timeline (sans company-private
 * comments).
 *
 * Recruitment workflow §5.5: every status shown — badges and the filter — is the
 * CANDIDATE-FACING status the backend sends ("Under Processing" while QuantumHire is still
 * reviewing the application internally). Withdraw was removed.
 *
 * URL bookmarkable: status, q, page round-trip via search params so a candidate
 * can deep-link to e.g. `/dashboard/applications?status=APPLIED&page=2`.
 *
 * StrictMode-safe fetch dedup: the `lastListKey.current` ref is pinned INSIDE the
 * fetch's `.then` (after cancel-check) so dev double-mount doesn't strand the page
 * empty (CLAUDE.md Frontend Conventions §).
 */

const PAGE_SIZE = 20;

const STATUS_OPTIONS: CandidateApplicationStatus[] = [
  'UNDER_PROCESSING',
  'APPLIED',
  'UNDER_REVIEW',
  'SHORTLISTED',
  'HIRED',
  'REJECTED',
  // No new withdrawals (§5.5), but older applications can still be Withdrawn.
  'WITHDRAWN',
];

export function MyApplicationsPage() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = (searchParams.get('status') as CandidateApplicationStatus | null) ?? null;
  const q = searchParams.get('q') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  // Debounce search input — keep an internal field and only push to URL after 300ms idle.
  const [qInput, setQInput] = useState(q);
  useEffect(() => {
    setQInput(q);
  }, [q]);
  useEffect(() => {
    if (qInput === q) return;
    const t = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (qInput) next.set('q', qInput);
          else next.delete('q');
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    }, 300);
    return () => clearTimeout(t);
  }, [qInput, q, setSearchParams]);

  const [items, setItems] = useState<ApplicationListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  const lastListKey = useRef<string | null>(null);

  // Detail drawer state
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [drawerData, setDrawerData] = useState<ApplicationDetail | null>(null);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const lastDrawerIdRef = useRef<string | null>(null);

  useEffect(() => {
    const key = JSON.stringify({ status, q, page });
    if (lastListKey.current === key) return;
    let cancelled = false;
    setItems(null);
    const params: ApplicationsListParams = { page, page_size: PAGE_SIZE };
    if (status) params.status = status;
    candidateApi.applications
      .list(params)
      .then((env) => {
        if (cancelled) return;
        lastListKey.current = key;
        const filtered = q
          ? env.data.filter((a) => {
              // Title and location only — the client company is never shown or matched on the
              // website (recruitment workflow §6.2).
              const haystack = [
                a.job?.title ?? '',
                a.job?.region?.name ?? '',
                ...(a.job?.cities ?? []).map((c) => c.name),
              ]
                .join(' ')
                .toLowerCase();
              return haystack.includes(q.toLowerCase());
            })
          : env.data;
        setItems(filtered);
        setTotal(env.meta.total);
        setTotalPages(env.meta.total_pages);
        setLoadError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.message : 'Could not load your applications.');
      });
    return () => {
      cancelled = true;
    };
  }, [status, q, page]);

  // Drawer fetch (StrictMode-safe — ref pinned AFTER fetch resolves).
  useEffect(() => {
    if (!drawerId) {
      setDrawerData(null);
      setDrawerError(null);
      lastDrawerIdRef.current = null;
      return;
    }
    if (lastDrawerIdRef.current === drawerId) return;
    let cancelled = false;
    setDrawerData(null);
    setDrawerError(null);
    candidateApi.applications
      .get(drawerId)
      .then((d) => {
        if (cancelled) return;
        lastDrawerIdRef.current = drawerId;
        setDrawerData(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDrawerError(
          err instanceof ApiError ? err.message : 'Could not load this application.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [drawerId]);

  const handleViewResume = useCallback(
    async (resumeId: string | null) => {
      if (!resumeId) return;
      try {
        const { url } = await candidateApi.resumes.downloadUrl(resumeId);
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Could not open this resume.');
      }
    },
    [toast],
  );

  const setPage = useCallback(
    (p: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('page', String(p));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setStatusFilter = useCallback(
    (next: CandidateApplicationStatus | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next) params.set('status', next);
          else params.delete('status');
          params.set('page', '1');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const columns = useMemo<Column<ApplicationListItem>[]>(
    () => [
      {
        key: 'job',
        header: 'Job',
        render: (row) => {
          const isDeleted = row.job?.status === 'DELETED';
          return (
            <div className="min-w-0">
              <p
                className={`font-bold truncate ${isDeleted ? 'text-gray-400 line-through' : 'text-brand-primary'}`}
              >
                {row.job?.title ?? '—'}
              </p>
              {isDeleted ? (
                <span className="inline-block mt-1 text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-1.5 py-0.5">
                  Job no longer available
                </span>
              ) : (
                <p className="text-xs text-brand-muted truncate">
                  {row.job?.region?.name ?? ''}
                  {row.job?.cities?.length ? ` · ${row.job.cities.map((c) => c.name).join(', ')}` : ''}
                </p>
              )}
            </div>
          );
        },
      },
      {
        key: 'applied',
        header: 'Applied',
        render: (row) => (
          <span className="text-xs text-brand-muted whitespace-nowrap">
            {formatRelativeTime(row.applied_at)}
          </span>
        ),
        width: '140px',
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => (
          <Badge tone={CANDIDATE_APPLICATION_STATUS_TONE[row.status] ?? 'gray'}>
            {CANDIDATE_APPLICATION_STATUS_LABELS[row.status] ?? row.status}
          </Badge>
        ),
        width: '140px',
      },
    ],
    [],
  );

  return (
    <section>
      <header className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-brand-primary">
          My Applications
        </h1>
        <p className="text-sm text-brand-muted mt-1">
          Track every job you've applied to. Click any row to see the full status timeline.
        </p>
      </header>

      {/* Filter bar */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-3 sm:p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 min-w-0">
          <input
            type="search"
            placeholder="Search applications…"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none"
          />
        </div>
        <div className="sm:w-56 shrink-0">
          <select
            value={status ?? ''}
            onChange={(e) => setStatusFilter((e.target.value || null) as CandidateApplicationStatus | null)}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm font-medium text-brand-primary focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none bg-white"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {CANDIDATE_APPLICATION_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loadError && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-2xl p-4 mb-4 text-sm">
          {loadError}
        </div>
      )}

      {items !== null && items.length === 0 ? (
        <EmptyState
          title="You haven't applied to any jobs yet"
          description="Browse the jobs feed and apply to roles that match your profile."
          icon={<Briefcase className="w-8 h-8 text-brand-muted" />}
          action={
            <Link
              to="/job-seekers"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-brand-deep transition-all"
            >
              Browse jobs
            </Link>
          }
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={items}
            rowKey={(r) => r.id}
            loading={items === null}
            onRowClick={(r) => setDrawerId(r.id)}
          />
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}

      <Drawer
        open={drawerId !== null}
        onClose={() => setDrawerId(null)}
        title={drawerData?.job?.title ?? 'Application'}
      >
        {drawerError && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm mb-4">
            {drawerError}
          </div>
        )}
        {!drawerData && !drawerError && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
          </div>
        )}
        {drawerData && (
          <ApplicationDetailView data={drawerData} onViewResume={handleViewResume} />
        )}
      </Drawer>

    </section>
  );
}

function ApplicationDetailView({
  data,
  onViewResume,
}: {
  data: ApplicationDetail;
  onViewResume: (resumeId: string | null) => void;
}) {
  const tone = CANDIDATE_APPLICATION_STATUS_TONE[data.status] ?? 'gray';
  const isDeletedJob = data.job?.status === 'DELETED';
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={tone}>
              {CANDIDATE_APPLICATION_STATUS_LABELS[data.status] ?? data.status}
            </Badge>
            {isDeletedJob && (
              <span className="inline-block text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-1.5 py-0.5">
                Job no longer available
              </span>
            )}
          </div>
          {data.status === 'UNDER_PROCESSING' && (
            <p className="text-sm text-brand-primary mt-2">
              Our team is reviewing your application before it&apos;s shared with the employer.
            </p>
          )}
          <p className="text-xs text-brand-muted mt-2">
            Applied {formatRelativeTime(data.applied_at)}
            {data.job?.region?.name ? ` · ${data.job.region.name}` : ''}
            {data.job?.cities?.length
              ? ` / ${data.job.cities.map((c) => c.name).join(', ')}`
              : ''}
          </p>
        </div>
        {data.job?.slug && !isDeletedJob && (
          <a
            href={`/job-seekers/${data.job.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-primary hover:text-brand-deep transition-colors"
          >
            View job posting <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </header>

      {data.resume && (
        <section className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
          <h3 className="text-xs font-black uppercase tracking-widest text-brand-muted mb-2">
            Attached resume
          </h3>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-brand-muted shrink-0" />
              <p className="text-sm text-brand-primary truncate">
                {data.resume.original_filename}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onViewResume(data.resume?.id ?? null)}
              className="text-xs font-bold text-brand-primary hover:text-brand-deep transition-colors whitespace-nowrap"
            >
              View resume
            </button>
          </div>
        </section>
      )}

      {data.job?.description_html && (
        <section className="bg-white border border-gray-200 rounded-2xl p-4">
          <h3 className="text-xs font-black uppercase tracking-widest text-brand-muted mb-3">
            About the role
          </h3>
          <div
            className="prose prose-sm max-w-none text-brand-primary"
            // Backend sanitizes job description HTML on write — see backend/src/common/sanitize.ts.
            dangerouslySetInnerHTML={{ __html: data.job.description_html }}
          />
        </section>
      )}

      <section className="bg-white border border-gray-200 rounded-2xl p-4">
        <h3 className="text-xs font-black uppercase tracking-widest text-brand-muted mb-3">
          Status timeline
        </h3>
        {data.status_history.length === 0 ? (
          <p className="text-sm text-brand-muted italic">No history yet.</p>
        ) : (
          <ol className="space-y-3">
            {[...data.status_history].reverse().map((row, i, arr) => (
              <li key={row.id} className="flex gap-3">
                <div className="flex flex-col items-center shrink-0">
                  <span className="w-2 h-2 rounded-full bg-brand-accent mt-1.5" />
                  {i < arr.length - 1 && <span className="w-px flex-1 bg-gray-200 my-1" />}
                </div>
                <div className="min-w-0 flex-1 pb-1">
                  <p className="text-xs text-brand-muted tabular-nums">
                    {formatRelativeTime(row.occurred_at)}
                  </p>
                  <p className="text-sm text-brand-primary">
                    Moved to{' '}
                    <span className="font-bold">
                      {APPLICATION_STATUS_LABELS[row.to_status as ApplicationStatus] ??
                        row.to_status}
                    </span>{' '}
                    {row.changed_by_user_type === 'CANDIDATE' && (
                      <span className="text-brand-muted">(by you)</span>
                    )}
                    {row.changed_by_user_type === 'SYSTEM' && (
                      <span className="text-brand-muted">(automatic)</span>
                    )}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
